import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { dark } from '@clerk/themes';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store/store';
import { setToken } from './store/authSlice';
import { App } from './App';
import { Toaster } from './components/ui/sonner';
import { ThemeProvider, useTheme } from './components/ThemeProvider/ThemeProvider';
import { worker } from './mocks/browser';
import './index.css';

async function enableMocking() {
  if (import.meta.env.MODE !== 'mock') {
    return;
  }

  return worker.start();
}

void enableMocking().then(() => {
  const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

  if (!CLERK_PUBLISHABLE_KEY) {
    throw new Error('Missing Clerk Publishable Key');
  }

  function AuthSync() {
    const { getToken, isLoaded } = useAuth();

    useEffect(() => {
      if (isLoaded) {
        getToken()
          .then((token) => {
            store.dispatch(setToken(token));
          })
          .catch(() => {
            store.dispatch(setToken(null));
          });
      }
    }, [getToken, isLoaded]);

    return null;
  }

  const rootElement = document.getElementById('root');

  if (!rootElement) {
    throw new Error('Root element not found');
  }

  function ThemedClerkProvider({ children }: { children: React.ReactNode }) {
    const { theme } = useTheme();

    const isDark =
      theme === 'dark' ||
      (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    return (
      <ClerkProvider
        publishableKey={CLERK_PUBLISHABLE_KEY}
        appearance={{
          baseTheme: isDark ? dark : undefined,
          variables: {
            colorPrimary: '#3b82f6',
            colorBackground: isDark ? '#141b2d' : '#ffffff',
          },
        }}
      >
        {children}
      </ClerkProvider>
    );
  }

  createRoot(rootElement).render(
    <StrictMode>
      <Provider store={store}>
        <ThemeProvider defaultTheme="system" storageKey="stashmark-theme">
          <ThemedClerkProvider>
            <AuthSync />
            <BrowserRouter>
              <App />
            </BrowserRouter>
            <Toaster />
          </ThemedClerkProvider>
        </ThemeProvider>
      </Provider>
    </StrictMode>
  );
});
